<script setup lang="ts">
import { computed, type PropType } from 'vue'

import { ensureFontAwesomeKit } from './fontAwesomeLoader'
import { fontAwesomeClasses } from './fontAwesomeNames'
import { LEGACY_RAIL_ICON_PRESENTATION } from './legacyRailPresentation'
import { type FontAwesomeWeight, type IconName } from './types'

const props = defineProps({
  name: {
    type: String as PropType<IconName>,
    required: true,
  },
  size: { type: Number, default: 16 },
  weight: { type: String as PropType<FontAwesomeWeight>, default: undefined },
  presentation: {
    type: String as PropType<'default' | 'legacy-rail'>,
    default: 'default',
  },
  label: { type: String, default: undefined },
})

ensureFontAwesomeKit()

const classes = computed(() => fontAwesomeClasses(props.name, props.presentation, props.weight))
const railPresentation = computed(() =>
  props.presentation === 'legacy-rail' ? LEGACY_RAIL_ICON_PRESENTATION[props.name] : undefined,
)
const renderedWidth = computed(() => railPresentation.value?.width ?? props.size)
const renderedHeight = computed(() => railPresentation.value?.height ?? props.size)
const opticalOffset = computed(() => {
  const x = railPresentation.value?.x ?? 0
  const y = railPresentation.value?.y ?? 0
  return x === 0 && y === 0 ? undefined : `translate(${x}px, ${y}px)`
})
</script>

<template>
  <i
    class="ui-icon inline-block flex-none text-center not-italic leading-none"
    :class="classes"
    :data-icon="name"
    :data-presentation="presentation"
    :style="{
      width: `${renderedWidth}px`,
      height: `${renderedHeight}px`,
      fontSize: `${size}px`,
      lineHeight: `${renderedHeight}px`,
      transform: opticalOffset,
    }"
    :aria-hidden="label ? undefined : 'true'"
    :aria-label="label"
    :role="label ? 'img' : undefined"
  />
</template>
